import React, { useCallback, useEffect, useState, useRef } from "react";
import {
  Box,
  IconButton,
  TextField,
  debounce,
  FormControlLabel,
  Checkbox,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  OutlinedInput,
  ListItemText,
  Tooltip,
  Alert,
  useTheme,
  Paper,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import "rsuite/dist/rsuite.min.css";
import { DateRangePicker } from "rsuite";
import RsuiteDateRangePickerWrapper from "components/common/global-style/GlobalStyleDatepicker";
import GoogleMapsAutocomplete from "./GoogleMapsAutocomplete";
import { validateAddress } from "../utils/addressUtility";
import { useJsApiLoader } from "@react-google-maps/api";
import {
  LOGISTICS_SEARCH_INFO_ALERT,
  SELECTED_WO_LOCS,
} from "../../common/constants/filterParamConstants";
import CloseIcon from "@mui/icons-material/Close";
import InfoIcon from "@mui/icons-material/Info";
import { fadeInUp } from "components/common/global-style/GlobalStyling";

type Props = {
  dateRange: [Date | null, Date | null] | null;
  searchText: string;
  setDateRange: (v: [Date | null, Date | null] | null) => void;
  setSearchText: (v: string) => void;
  onReset: () => void;
  WorkOrderStatuses: { identity: number; displayText: string }[];
  WorkOrderStatusIds: string[]; // Empty array means All
  setWorkOrderStatusIds: (v: string[]) => void;
  locations: { identity: number; displayText: string }[];
  locationId: string; // "0" means All
  setLocationId: (v: string) => void;
  startingPoint: string;
  setStartingPoint: (v: string) => void;
  defaultStartingPoint: string; // User's default location address
  includeRentalContracts: boolean;
  setIncludeRentalContracts: (v: boolean) => void;
  onStartingPointSelect?: (
    address: string,
    coordinates?: { lat: number; lng: number },
  ) => void;
  onStartingPointTyping?: () => void;
  startingPointError?: boolean;
  startingPointErrorText?: string;
  pickupDateRange: [Date | null, Date | null] | null;
  setPickupDateRange: (v: [Date | null, Date | null] | null) => void;
  deliveryDateRange: [Date | null, Date | null] | null;
  setDeliveryDateRange: (v: [Date | null, Date | null] | null) => void;
};

const LogisticsFilters: React.FC<Props> = ({
  dateRange,
  searchText,
  setDateRange,
  setSearchText,
  onReset,
  WorkOrderStatuses,
  WorkOrderStatusIds,
  setWorkOrderStatusIds,
  locations,
  locationId,
  setLocationId,
  startingPoint,
  setStartingPoint,
  defaultStartingPoint,
  includeRentalContracts,
  setIncludeRentalContracts,
  onStartingPointSelect,
  onStartingPointTyping,
  startingPointError,
  startingPointErrorText,
  pickupDateRange,
  setPickupDateRange,
  deliveryDateRange,
  setDeliveryDateRange,
}) => {
  const [localSearch, setLocalSearch] = useState<string>(searchText || "");
  const [localStartingPoint, setLocalStartingPoint] = useState<string>(
    startingPoint || "",
  );
  const [isSelecting, setIsSelecting] = useState(false);
  const hasValidatedInitialStartingPoint = useRef(false);
  const lastStartingPointFromParent = useRef<string>(startingPoint || ""); // Track last value set by parent
  const [userName] = useState<string>(
    () => localStorage.getItem("username") || "",
  );

  // Check if Google Maps is loaded for validation
  const { isLoaded: isGoogleMapsLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDipPUXM5YzthCmrSesDjYPvc6-LmfBByk",
    libraries: ["places"],
  });

  // Debounce updating parent searchText to avoid firing on every key press
  const debouncedSetSearch = useCallback(
    debounce((value: string) => {
      setSearchText(value);
    }, 400),
    [setSearchText],
  );

  // Debounce updating parent startingPoint to avoid firing on every key press
  // Validates address before binding - if invalid, sets empty string
  const debouncedSetStartingPoint = useCallback(
    debounce(async (value: string) => {
      //console.log("[debouncedSetStartingPoint] Called with value:", value);
      if (!value || !value.trim()) {
        //console.log("[debouncedSetStartingPoint] Empty value, clearing");
        setStartingPoint("");
        return;
      }

      // Validate address before setting
      if (isGoogleMapsLoaded) {
        try {
          // console.log(
          //   "[debouncedSetStartingPoint] Validating address:",
          //   value.trim()
          // );
          const coordinates = await validateAddress(value.trim());
          if (coordinates) {
            // Address is valid, bind it
            // console.log(
            //   "[debouncedSetStartingPoint] Validation SUCCESS, setting startingPoint to:",
            //   value.trim()
            // );
            setStartingPoint(value.trim());
          } else {
            // Address is invalid, bind empty string
            // console.log(
            //   "[debouncedSetStartingPoint] Validation FAILED (no coordinates), clearing"
            // );
            setStartingPoint("");
          }
        } catch (error) {
          console.error(
            "[debouncedSetStartingPoint] Error validating address:",
            error,
          );
          // On error, set empty string
          setStartingPoint("");
        }
      } else {
        // If Google Maps not loaded yet, set empty string
        // console.log(
        //   "[debouncedSetStartingPoint] Google Maps not loaded, clearing"
        // );
        setStartingPoint("");
      }
    }, 2000), // 2 seconds delay for starting point
    [setStartingPoint, isGoogleMapsLoaded],
  );

  useEffect(() => {
    // keep local in sync if parent resets/changes searchText externally
    setLocalSearch(searchText || "");
  }, [searchText]);

  useEffect(() => {
    // keep local in sync if parent resets/changes startingPoint externally
    // console.log(
    //   "[LogisticsFilters] startingPoint prop changed to:",
    //   startingPoint
    // );
    // Track when parent sets a new value (different from last known value)
    if (startingPoint !== lastStartingPointFromParent.current) {
      // console.log(
      //   "[LogisticsFilters] Parent set new startingPoint value, updating local state"
      // );
      lastStartingPointFromParent.current = startingPoint || "";
      setLocalStartingPoint(startingPoint || "");
    }
  }, [startingPoint]);

  // Validate initial startingPoint on page load
  // NOTE: Skip validation if the value was just set by parent (parent already validated it)
  useEffect(() => {
    const validateInitialStartingPoint = async () => {
      // console.log(
      //   "[validateInitialStartingPoint] Effect triggered, startingPoint:",
      //   startingPoint,
      //   "lastFromParent:",
      //   lastStartingPointFromParent.current
      // );
      // Only validate once on initial load
      if (hasValidatedInitialStartingPoint.current) {
        // console.log(
        //   "[validateInitialStartingPoint] Already validated, skipping"
        // );
        return;
      }

      // Only validate if Google Maps is loaded
      if (!isGoogleMapsLoaded) {
        // console.log(
        //   "[validateInitialStartingPoint] Google Maps not loaded, skipping"
        // );
        return;
      }

      // Mark as validated if no starting point value
      if (!startingPoint || !startingPoint.trim()) {
        // console.log(
        //   "[validateInitialStartingPoint] No starting point value, marking as validated"
        // );
        hasValidatedInitialStartingPoint.current = true;
        return;
      }

      // Skip validation if this value was just set by parent (parent already validated it)
      // This prevents double-validation and race conditions
      if (
        startingPoint === lastStartingPointFromParent.current &&
        lastStartingPointFromParent.current !== ""
      ) {
        // console.log(
        //   "[validateInitialStartingPoint] Value was set by parent (already validated), skipping validation"
        // );
        hasValidatedInitialStartingPoint.current = true;
        return;
      }

      // Validate the initial starting point value
      // console.log(
      //   "[validateInitialStartingPoint] Validating initial starting point:",
      //   startingPoint.trim()
      // );
      hasValidatedInitialStartingPoint.current = true;
      try {
        const coordinates = await validateAddress(startingPoint.trim());
        if (!coordinates) {
          // Address is invalid, set to empty string
          // console.log(
          //   "[validateInitialStartingPoint] Validation FAILED (no coordinates), clearing startingPoint"
          // );
          setStartingPoint("");
          setLocalStartingPoint("");
        } else {
          // console.log(
          //   "[validateInitialStartingPoint] Validation SUCCESS, keeping startingPoint"
          // );
        }
        // If valid, keep the value as is
      } catch (error) {
        console.error(
          "[validateInitialStartingPoint] Error validating initial starting point:",
          error,
        );
        // On error, set to empty string
        // console.log(
        //   "[validateInitialStartingPoint] Error occurred, clearing startingPoint"
        // );
        setStartingPoint("");
        setLocalStartingPoint("");
      }
    };

    validateInitialStartingPoint();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGoogleMapsLoaded, startingPoint]);

  // Date validation function
  const isValidDate = (date: any) => {
    return date instanceof Date && !isNaN(date.getTime());
  };

  // Date change handler (similar to FilterPanel implementation)
  const handleDateChange = (value: [Date | null, Date | null] | null) => {
    if (!value || !Array.isArray(value) || value.length !== 2) {
      setDateRange(null);
      return;
    }

    const [startDate, endDate] = value;

    // Allow partial input (only one date filled) - don't update parent state
    if (!isValidDate(startDate) || !isValidDate(endDate)) {
      setDateRange(value);
      return;
    }

    // Ensure second date is after the first date
    if (endDate.getTime() >= startDate.getTime()) {
      setDateRange(value);
    }
  };
  // Alert state
  const [showLogisticsAlert, setShowLogisticsAlert] = useState<boolean>(() => {
    const userName = localStorage.getItem("username") || "";
    return (
      localStorage.getItem(`${LOGISTICS_SEARCH_INFO_ALERT}_${userName}`) !==
      "false"
    );
  });
  const theme = useTheme();

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        borderRadius: "8px",
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.mode === "dark" ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"}`,
        boxShadow:
          theme.palette.mode === "dark"
            ? "0 4px 24px rgba(0,0,0,0.3)"
            : "0 4px 24px rgba(0,0,0,0.06)",
        animation: `${fadeInUp} 0.5s ease-out 0.2s both`,
      }}
    >
      {/* Alert Banner */}
      {showLogisticsAlert && (
        <Alert
          severity="info"
          variant="outlined"
          icon={<InfoIcon />}
          sx={{
            mb: 2.5,
            borderRadius: "12px",
            backgroundColor:
              theme.palette.mode === "dark"
                ? "rgba(41, 182, 246, 0.08)"
                : "rgba(33, 150, 243, 0.06)",
            borderColor:
              theme.palette.mode === "dark"
                ? "rgba(41, 182, 246, 0.3)"
                : "rgba(33, 150, 243, 0.25)",
            "& .MuiAlert-message": {
              fontSize: "0.875rem",
              color: theme.palette.text.primary,
            },
            "& .MuiAlert-icon": {
              color: theme.palette.info.main,
            },
          }}
          action={
            <IconButton
              aria-label="close"
              size="small"
              onClick={() => {
                setShowLogisticsAlert(false);
                const userName = localStorage.getItem("username") || "";
                localStorage.setItem(
                  `${LOGISTICS_SEARCH_INFO_ALERT}_${userName}`,
                  "false",
                );
              }}
            >
              <CloseIcon fontSize="inherit" />
            </IconButton>
          }
        >
          Showing results from the last 6 months by default. Adjust date range
          filter to refine your search.
        </Alert>
      )}

      <RsuiteDateRangePickerWrapper />
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          alignItems: "flex-end",
        }}
      >
        <DateRangePicker
          value={dateRange}
          onChange={handleDateChange}
          size="md"
          placeholder="WO Creation Date Range"
          format="MM/dd/yyyy"
          character=" - "
          style={{ width: "12%", height: "33px", marginBottom: "3px" }}
          shouldDisableDate={(date: any, selectedIndex: any) => {
            if (selectedIndex === 1 && dateRange?.[0]) {
              return date.getTime() < dateRange[0]!.getTime(); // Ensure second date is greater
            }
            return false;
          }}
        />
        <DateRangePicker
          value={pickupDateRange}
          onChange={(value) => setPickupDateRange(value)}
          size="md"
          placeholder="Pickup Date Range"
          format="MM/dd/yyyy"
          character=" - "
          style={{ width: "12%", height: "33px", marginBottom: "3px" }}
          onClean={() => setPickupDateRange(null)}
        />
        <DateRangePicker
          value={deliveryDateRange}
          onChange={(value) => setDeliveryDateRange(value)}
          size="md"
          placeholder="Delivery Date Range"
          format="MM/dd/yyyy"
          character=" - "
          style={{ width: "12%", height: "33px", marginBottom: "3px" }}
          onClean={() => setDeliveryDateRange(null)}
        />
        <TextField
          label="Search Doc no, customer, status..."
          placeholder="Doc no, customer, status..."
          size="small"
          value={localSearch}
          onChange={(e) => {
            const val = e.target.value;
            setLocalSearch(val);
            debouncedSetSearch(val);
          }}
          sx={{ width: 180 }}
        />
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel id="wo-status-label">Work Order Status</InputLabel>
          <Select
            labelId="wo-status-label"
            multiple
            value={WorkOrderStatusIds}
            label="Work Order Status"
            onChange={(e) => {
              const value = e.target.value;
              setWorkOrderStatusIds(
                typeof value === "string" ? value.split(",") : value,
              );
            }}
            input={<OutlinedInput label="Work Order Status" />}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  overflowY: "auto",
                },
              },
            }}
            renderValue={(selected) => {
              if (selected.length === 0) return "All";
              const selectedStatuses = WorkOrderStatuses.filter((s) =>
                selected.includes(String(s.identity)),
              );
              if (selectedStatuses.length === 1) {
                return selectedStatuses[0].displayText;
              }
              return `${selectedStatuses.length} selected`;
            }}
          >
            {WorkOrderStatuses.map((s) => (
              <MenuItem key={s.identity} value={String(s.identity)}>
                <Checkbox
                  checked={WorkOrderStatusIds.includes(String(s.identity))}
                  size="small"
                />
                <ListItemText primary={s.displayText} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel size="small" id="location-label">
            Location
          </InputLabel>
          <Select
            labelId="location-label"
            value={locationId}
            label="Location"
            size="small"
            onChange={(e) => {
              const value = String(e.target.value);
              setLocationId(value);
              localStorage.setItem(`${SELECTED_WO_LOCS}_${userName}`, value);
            }}
            displayEmpty
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 300,
                  overflowY: "auto",
                },
              },
            }}
            renderValue={(value) => {
              if (value === "0") return "All";
              const found = locations.find(
                (l) => String(l.identity) === String(value),
              );
              return found ? found.displayText : "All";
            }}
          >
            <MenuItem value={"0"}>All</MenuItem>
            {locations.map((l) => (
              <MenuItem key={l.identity} value={String(l.identity)}>
                {l.displayText}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box sx={{ position: "relative" }}>
          <GoogleMapsAutocomplete
            label="Starting Point"
            placeholder="Enter starting address..."
            size="small"
            value={localStartingPoint}
            onChange={(val) => {
              // console.log(
              //   "[GoogleMapsAutocomplete onChange] Value changed to:",
              //   val,
              //   "isSelecting:",
              //   isSelecting
              // );
              setLocalStartingPoint(val);
              // Only call debouncedSetStartingPoint if user is typing (not when value is set by parent)
              // Check if the value is different from the current startingPoint prop to detect user input
              if (!isSelecting && val !== startingPoint) {
                // console.log(
                //   "[GoogleMapsAutocomplete onChange] User typing detected, calling debouncedSetStartingPoint"
                // );
                debouncedSetStartingPoint(val);
              } else {
                // console.log(
                //   "[GoogleMapsAutocomplete onChange] Skipping debouncedSetStartingPoint (isSelecting or same as prop)"
                // );
              }
              // Clear selected work orders when user starts typing in starting point
              if (onStartingPointTyping && val !== startingPoint) {
                onStartingPointTyping();
              }
            }}
            onSelect={async (address, coordinates) => {
              // console.log(
              //   "[GoogleMapsAutocomplete onSelect] Address selected:",
              //   address,
              //   "coordinates:",
              //   coordinates
              // );
              setIsSelecting(true);
              // Set local state immediately for UI feedback
              setLocalStartingPoint(address);

              // Validate address before binding to parent
              if (coordinates) {
                // If coordinates provided from autocomplete, address is valid
                // console.log(
                //   "[GoogleMapsAutocomplete onSelect] Coordinates provided, setting startingPoint to:",
                //   address
                // );
                setStartingPoint(address);
                if (onStartingPointSelect) {
                  onStartingPointSelect(address, coordinates);
                }
              } else if (isGoogleMapsLoaded) {
                // If no coordinates, validate the address
                try {
                  // console.log(
                  //   "[GoogleMapsAutocomplete onSelect] No coordinates, validating address:",
                  //   address
                  // );
                  const validatedCoordinates = await validateAddress(address);
                  if (validatedCoordinates) {
                    // Address is valid, bind it
                    // console.log(
                    //   "[GoogleMapsAutocomplete onSelect] Validation SUCCESS, setting startingPoint to:",
                    //   address
                    // );
                    setStartingPoint(address);
                    if (onStartingPointSelect) {
                      onStartingPointSelect(address, validatedCoordinates);
                    }
                  } else {
                    // Address is invalid, bind empty string
                    // console.log(
                    //   "[GoogleMapsAutocomplete onSelect] Validation FAILED, clearing startingPoint"
                    // );
                    setStartingPoint("");
                    if (onStartingPointSelect) {
                      onStartingPointSelect("", undefined);
                    }
                  }
                } catch (error) {
                  console.error(
                    "[GoogleMapsAutocomplete onSelect] Error validating address:",
                    error,
                  );
                  // On error, set empty string
                  // console.log(
                  //   "[GoogleMapsAutocomplete onSelect] Error occurred, clearing startingPoint"
                  // );
                  setStartingPoint("");
                  if (onStartingPointSelect) {
                    onStartingPointSelect("", undefined);
                  }
                }
              } else {
                // If Google Maps not loaded, set empty string
                // console.log(
                //   "[GoogleMapsAutocomplete onSelect] Google Maps not loaded, clearing startingPoint"
                // );
                setStartingPoint("");
                if (onStartingPointSelect) {
                  onStartingPointSelect("", undefined);
                }
              }

              // Reset the flag after a short delay
              setTimeout(() => setIsSelecting(false), 100);
            }}
            sx={{
              width: 220,
              ...(startingPointError
                ? {
                    "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: (theme: any) => theme.palette.error.main,
                      },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline":
                      {
                        borderColor: (theme: any) => theme.palette.error.dark,
                      },
                    "& .MuiInputLabel-root": {
                      color: (theme: any) => theme.palette.error.main,
                    },
                  }
                : {}),
            }}
            inputProps={{
              title: startingPointError
                ? startingPointErrorText ||
                  "Invalid address. Please select from suggestions."
                : undefined,
            }}
          />
        </Box>
        <FormControlLabel
          control={
            <Checkbox
              checked={includeRentalContracts}
              onChange={(e) => setIncludeRentalContracts(e.target.checked)}
              size="small"
            />
          }
          label="Include Rental Contracts"
          sx={{ minWidth: 200 }}
        />
        <Tooltip title="Reset Filters">
          <IconButton
            size="small"
            onClick={() => {
              setDateRange(null);
              setLocalSearch("");
              setSearchText("");
              setWorkOrderStatusIds([]);
              setLocationId("0");
              localStorage.setItem(`${SELECTED_WO_LOCS}_${userName}`, "0");
              setLocalStartingPoint(defaultStartingPoint);
              setStartingPoint(defaultStartingPoint);
              setIncludeRentalContracts(false);
              setPickupDateRange(null);
              setDeliveryDateRange(null);
              hasValidatedInitialStartingPoint.current = false;
              lastStartingPointFromParent.current = "";
              onReset();
            }}
            sx={{
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default LogisticsFilters;
